// ─────────────────────────────────────────────
//  useRoomAutoRelease — a room that finished its last case lets go of itself.
//
//  Owner, 2026-09-09: *"When the black time line pass the end of the or time and
//  no more cases to follow. Wait for m15 min, clear the room and remove it from
//  the time line and send the crna to unassigned"* — and everybody in it, not
//  only the seat: *"All the option"* (the CRNA, the second CRNA and the
//  supervising doctor).
//
//  WHAT IT WRITES, AND WHAT IT DELIBERATELY DOES NOT.
//
//  ⚠️ THE ASSIGNMENT ROW IS NEVER TOUCHED. Owner's choice when it was put to
//  him: the row is the day's RECORD of who ran that case, and clearing `ahpId`
//  would answer "the board is tidy" by destroying the answer to "who was in WOR
//  5 this morning". `clearRoom` empties the ROOM — the thing that describes what
//  is happening now — and the row keeps the history.
//
//  ⚠️ NOBODY IS WRITTEN TO THE BENCH, BECAUSE THE BENCH IS DERIVED. `benchProviders`
//  is roster-minus-assigned through `rowClaimsAt`, which already stops counting a
//  finished row's people after the same 15 minutes. They are on FREE NOW before
//  this hook does anything; a second write naming them would be a second
//  definition of "free", and the two would disagree the first time either moved.
//  The only roster write here is `freeSince`, which is a REPORTING stamp: it is
//  what turns "free" into "free since 14:15" instead of TBD.
//
//  ⚠️ IT REUSES `clearRoom`, THE BOARD'S OWN CONTROL. That is the same write the
//  ✕ on a RoomCard makes, so an automatically-closed room and a hand-closed one
//  end in exactly the same state. A private copy of the field list here would
//  drift the first time somebody added a day-scoped field to the room doc.
//
//  ── WHY IT IS SAFE TO RUN THIS FROM A BROWSER ────────────────────────────
//
//  Every board open on the ward runs this hook. That is fine, and it is fine for
//  a reason rather than by luck:
//    · it only runs for a viewer who may already write to the board;
//    · the write is idempotent — `clearRoom` sets a room to a fixed state, so a
//      second board making it changes nothing;
//    · each room is attempted ONCE per session (`doneRef`), so a failing write
//      cannot become a loop against Firestore on a 60-second clock;
//    · and the domain rule that selects the rooms is pure and shares the board's
//      one buffer, so two boards cannot disagree about which rooms qualify.
//  What it must never become is a retry loop: a room that fails to clear is left
//  to the human, and the ✕ on its card is still there.
// ─────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clearRoom, setRosterMemberFreeSince } from "../../firebase/firestore";
import { releasedPeople, roomReleaseRows } from "../../domain/roomRelease";

// ⚠️ THE HOOK ASKS THE DOMAIN, RATHER THAN BEING HANDED THE ANSWER, AND THAT IS
// NOT A STYLE CHOICE. "Keep it open" is state that lives here, and it is also an
// INPUT to the rule that decides which rooms qualify. Split across two owners —
// the page computing the rows, the hook holding the holds — the page would need
// the hook's state to build the hook's arguments, and the loop only resolves by
// letting one of them go a render stale. A room appearing for one frame in a
// list it has been withdrawn from is a room that gets cleared after somebody
// pressed Keep it open.
export default function useRoomAutoRelease({
  // The chart's own sections — `buildTimeline` has already decided which rooms
  // are occupied and what every case's minutes are.
  sections = [],
  // (roomId) => that room doc's status string, or null.
  roomStatus = () => null,
  // (roomId) => when that room doc was last written, in minutes since midnight
  // on the same clock as `nowMin`, or null. A status that cannot be dated cannot
  // hold a room — see `domain/roomRelease.statusHoldsRoom` for why that is the
  // safe direction rather than the reckless one.
  roomStatusAt = () => null,
  // Minutes since midnight, facility-local, or null on a day that is not today.
  // Null means the domain has no opinion and nothing is ever cleared — which is
  // what makes looking at yesterday's board safe.
  nowMin = null,
  // May this viewer write to the board at all? A wall display and an observer
  // must never clear a room.
  enabled = false,
  facilityId = null,
  date = null,
  updatedBy = null,
  // Reported, never thrown: the clinical act is the room clearing, and a toast
  // is how a refusal becomes visible instead of a control that did nothing.
  onError = null,
} = {}) {
  // Rooms a human answered "Keep it open" on. Session-scoped on purpose: it is
  // an answer about what is happening in that room RIGHT NOW, and a stale hold
  // that outlived the case would keep a finished room on the board all night
  // with nobody able to see why.
  const [heldIds, setHeldIds] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  // Rooms this session has already attempted. NOT state: it must not re-render,
  // and it must be read inside the effect without becoming a dependency.
  const doneRef = useRef(new Set());

  // The rule, asked with the holds folded in. `heldIds` is a dependency, so a
  // room drops out of both lists the moment somebody keeps it open.
  const { releaseIds, releaseRows, confirmRows } = useMemo(
    () => roomReleaseRows({ sections, roomStatus, roomStatusAt, nowMin, heldIds }),
    [sections, roomStatus, roomStatusAt, nowMin, heldIds]
  );

  // roomId → that room's cases, for naming who is being released.
  const casesById = useMemo(() => {
    const m = new Map();
    for (const r of [...releaseRows, ...confirmRows]) m.set(r.roomId, r.cases);
    return m;
  }, [releaseRows, confirmRows]);

  const releaseRoom = useCallback(
    async (roomId) => {
      if (!roomId) return false;
      const people = releasedPeople(casesById.get(roomId) || []);
      await clearRoom(roomId);
      // ⚠️ THE STAMP IS BEST-EFFORT AND MUST NOT UNDO THE CLEAR. The room is the
      // clinical fact and it has already landed; a failed stamp degrades to TBD
      // on FREE NOW, which is the honest fallback this app already uses
      // everywhere else. `id` is null for anybody the importer never resolved —
      // `releasedPeople` strips the synthetic `sp:` key precisely so a roster
      // write cannot be keyed by one and land on a document that does not exist.
      if (facilityId && date) {
        for (const p of people) {
          if (!p.id) continue;
          try {
            await setRosterMemberFreeSince(facilityId, date, p.id, updatedBy || null);
          } catch (err) {
            console.error("Room release: freeSince stamp failed:", err);
          }
        }
      }
      return true;
    },
    [casesById, facilityId, date, updatedBy]
  );

  // The automatic path.
  useEffect(() => {
    if (!enabled) return;
    const todo = [...releaseIds].filter((id) => id && !doneRef.current.has(id));
    if (todo.length === 0) return;
    // Marked BEFORE the await, not after. Two ticks of the clock can overlap one
    // slow write, and "attempt once" has to mean once even then.
    for (const id of todo) doneRef.current.add(id);
    let cancelled = false;
    (async () => {
      for (const id of todo) {
        try {
          await releaseRoom(id);
        } catch (err) {
          console.error("Room release failed:", err);
          if (!cancelled && onError) onError(err, id);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, releaseIds, releaseRoom, onError]);

  // The two answers to the warning card.
  const closeRoom = useCallback(
    async (roomId) => {
      if (!roomId) return;
      setBusyId(roomId);
      try {
        doneRef.current.add(roomId);
        await releaseRoom(roomId);
      } catch (err) {
        console.error("Room close failed:", err);
        // ⚠️ ATTEMPTABLE AGAIN. A room the human asked to close and that failed
        // must not be silently marked done — the card is where they would see
        // it, and it has to come back.
        doneRef.current.delete(roomId);
        if (onError) onError(err, roomId);
      } finally {
        setBusyId(null);
      }
    },
    [releaseRoom, onError]
  );

  const keepOpen = useCallback((roomId) => {
    if (!roomId) return;
    setHeldIds((prev) => {
      if (prev.has(roomId)) return prev;
      const next = new Set(prev);
      next.add(roomId);
      return next;
    });
  }, []);

  // ⚠️ `confirmRows` CARRIES THE PEOPLE, not only the room. The card names who
  // it is about to release, because "and 3 others" is not something an AIC can
  // check before pressing a button that sends them all to the free list.
  const warnRows = useMemo(
    () => confirmRows.map((r) => ({ ...r, people: releasedPeople(r.cases) })),
    [confirmRows]
  );

  return { releaseIds, confirmRows: warnRows, heldIds, busyId, closeRoom, keepOpen };
}
